// src/components/Navigation/MobileBottomNav.js
import React from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router';

// Import icons from Material-UI
import InsightsIcon from '@mui/icons-material/Insights';
import CreateIcon from '@mui/icons-material/Create';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';

// Import navigation config
import { NAVIGATION_CONFIG } from '../../config/navigationConfig';

/**
 * Mobile Bottom Navigation Bar
 *
 * Displays a fixed bottom navigation bar on mobile devices with 4 primary actions:
 * - Insights (Dashboard)
 * - Capture (Special handling - triggers callback)
 * - Care
 * - Faana AI (Chat)
 *
 * @param {Object} props
 * @param {Function} props.onCaptureClick - Callback function when Capture is tapped
 */
function MobileBottomNav({ onCaptureClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Icon mapping - matches approved icons from navigationConfig
  const iconMap = {
    'InsightsIcon': InsightsIcon,
    'CreateIcon': CreateIcon,
    'SmartToyIcon': SmartToyIcon,
    'AccountTreeIcon': AccountTreeIcon,
    'VolunteerActivismIcon': VolunteerActivismIcon,
  };

  // Mobile bottom nav shows only 4 items in this order: Insights, Care, Capture, Faana AI
  const mobileNavOrder = ['insights', 'care', 'capture', 'chat'];
  const mobileNavItems = mobileNavOrder
    .map(id => NAVIGATION_CONFIG.primary.find(item => item.id === id))
    .filter(Boolean);

  // Don't render on non-mobile devices
  if (!isMobile) {
    return null;
  }

  // Get current value based on route
  const getCurrentValue = () => {
    const currentPath = location.pathname;

    // Find matching primary nav item by route
    const matchedItem = NAVIGATION_CONFIG.primary.find(item => {
      if (item.route === currentPath) return true;
      // Handle route aliases (e.g., /client-management for Entities)
      if (item.id === 'entities' && currentPath === '/client-management') return true;
      return false;
    });

    return matchedItem ? matchedItem.id : 'insights';
  };

  const handleChange = (event, newValue) => {
    // Special case: Capture triggers callback instead of navigation
    if (newValue === 'capture') {
      if (onCaptureClick) {
        onCaptureClick();
      }
      return;
    }

    // Find the nav item and navigate to its route
    const navItem = NAVIGATION_CONFIG.primary.find(item => item.id === newValue);
    if (navItem) {
      navigate(navItem.route);
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
      }}
      elevation={8}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 56,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 12px',
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.75rem',
            '&.Mui-selected': {
              fontSize: '0.75rem',
            },
          },
          '& .MuiSvgIcon-root': {
            fontSize: 32,
          },
        }}
      >
        {mobileNavItems.map(item => {
          const IconComponent = iconMap[item.icon];

          return (
            <BottomNavigationAction
              key={item.id}
              label={item.label}
              value={item.id}
              icon={<IconComponent />}
              sx={{
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              }}
            />
          );
        })}
      </BottomNavigation>
    </Paper>
  );
}

export default MobileBottomNav;
